import {
  AutocompleteInput,
  DateInput,
  ReferenceInput,
  SelectInput,
  TextInput,
} from "@/components/admin";
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const statusChoices = [
  { id: "active", name: "Active" },
  { id: "inactive", name: "Inactive" },
  { id: "terminated", name: "Terminated" },
  { id: "on_leave", name: "On Leave" },
];

const employmentTypeChoices = [
  { id: "full_time", name: "Full Time" },
  { id: "part_time", name: "Part Time" },
  { id: "contract", name: "Contract" },
  { id: "intern", name: "Intern" },
];

const genderChoices = [
  { id: "Male", name: "Male" },
  { id: "Female", name: "Female" },
  { id: "Other", name: "Other" },
];

const bloodGroupChoices = [
  { id: "A+", name: "A+" },
  { id: "A-", name: "A-" },
  { id: "B+", name: "B+" },
  { id: "B-", name: "B-" },
  { id: "O+", name: "O+" },
  { id: "O-", name: "O-" },
  { id: "AB+", name: "AB+" },
  { id: "AB-", name: "AB-" },
];

const CollapsibleSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="w-full border rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        {open ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        {title}
      </button>
      {open && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {children}
        </div>
      )}
    </div>
  );
};

export const EmployeeCoreInputs = () => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <TextInput source="employee_code" label="Employee Code" />
      <TextInput source="name" label="Full Name" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <TextInput source="email" label="Email" />
      <TextInput source="phone" label="Phone" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <ReferenceInput source="department_id" reference="hr_departments">
        <AutocompleteInput label="Department" optionText="name" />
      </ReferenceInput>
      <ReferenceInput source="designation_id" reference="hr_designations">
        <AutocompleteInput label="Designation" optionText="title" />
      </ReferenceInput>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <ReferenceInput source="manager_id" reference="employees">
        <AutocompleteInput label="Manager" optionText="name" />
      </ReferenceInput>
      <ReferenceInput source="sales_id" reference="sales">
        <AutocompleteInput label="Linked Login User" optionText="email" />
      </ReferenceInput>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <DateInput source="joining_date" label="Joining Date" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <SelectInput
        source="status"
        label="Status"
        choices={statusChoices}
        defaultValue="active"
      />
      <SelectInput
        source="employment_type"
        label="Employment Type"
        choices={employmentTypeChoices}
        defaultValue="full_time"
      />
    </div>
  </>
);

export const EmployeePersonalInputs = () => (
  <CollapsibleSection title="Personal Information">
    <SelectInput source="gender" label="Gender" choices={genderChoices} />
    <DateInput source="dob" label="Date of Birth" />
    <SelectInput
      source="blood_group"
      label="Blood Group"
      choices={bloodGroupChoices}
    />
  </CollapsibleSection>
);

export const EmployeeGovernmentInputs = () => (
  <CollapsibleSection title="Government IDs">
    <TextInput source="pan" label="PAN Number" />
    <TextInput source="aadhaar" label="Aadhaar Number" />
  </CollapsibleSection>
);

export const EmployeeBankInputs = () => (
  <CollapsibleSection title="Bank Details">
    <TextInput source="bank_details.account_name" label="Account Name" />
    <TextInput source="bank_details.account_number" label="Account Number" />
    <TextInput source="bank_details.ifsc" label="IFSC" />
    <TextInput source="bank_details.bank_name" label="Bank Name" />
  </CollapsibleSection>
);
